import React, { useState } from 'react';
import { TextInput, TextInputProps, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useT } from '@transifex/react';

import { useDsEnabled } from '../contexts/DesignSystem';
import { testId } from '../utils/testId';
import { useThemePalette } from '../contexts/ThemePalette';
import { cn } from '../utils/cn';

import { BaseButton } from './BaseButton';
import { DsField } from './DsField';
import ThemedText from './ThemedText';

interface PasswordInputProps extends Omit<TextInputProps, 'style'> {
  label: string;
  error?: string;
  containerStyle?: any;
  // vida-ds only: rendered at the right of the label row.
  labelAccessory?: React.ReactNode;
  errorTestID?: string;
}

const EyeIcon: React.FC<{ isVisible: boolean; color: string }> = ({ isVisible, color }) => {
  if (!isVisible) {
    return (
      <Svg width={40} height={40} viewBox='0 0 24 24' fill='none'>
        <Path
          d='M12.9722 12.0002C12.9722 12.5374 12.5368 12.9728 11.9997 12.9728C11.4625 12.9728 11.0271 12.5374 11.0271 12.0002C11.0271 11.4631 11.4625 11.0277 11.9997 11.0277C12.5368 11.0277 12.9722 11.4631 12.9722 12.0002Z'
          fill={color}
        />
        <Path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M17.5589 11.2825C16.2726 9.8674 14.1393 8.3716 11.9997 8.4017C9.86 8.3711 7.7267 9.8679 6.4404 11.2825C6.0754 11.6906 6.0754 12.3079 6.4404 12.716C7.7116 14.1161 9.8148 15.5987 11.9214 15.5987H12.0711C14.185 15.5987 16.2877 14.1161 17.5603 12.7156C17.9249 12.3072 17.9243 11.6901 17.5589 11.2825ZM9.7627 12.0002C9.7627 10.7648 10.7642 9.7633 11.9997 9.7633C13.2351 9.7633 14.2366 10.7648 14.2366 12.0002C14.2366 13.2356 13.2351 14.2371 11.9997 14.2371C10.7642 14.2371 9.7627 13.2356 9.7627 12.0002Z'
          fill={color}
        />
      </Svg>
    );
  }

  return (
    <Svg width={40} height={40} viewBox='0 0 24 24' fill='none'>
      <Path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M17.5565 11.2849C16.9285 10.5965 16.2108 9.9956 15.4228 9.4985L17.4076 7.516C17.5366 7.3914 17.5884 7.2069 17.543 7.0335C17.4976 6.86 17.3621 6.7245 17.1886 6.6791C17.0151 6.6336 16.8306 6.6854 16.706 6.8144L16.6971 6.8233C16.68 6.8775 16.6506 6.9269 16.6112 6.9677L6.9348 16.6441C6.933 16.6457 6.931 16.6468 6.9289 16.6478C6.9265 16.6491 6.9241 16.6504 6.9219 16.6525C6.8322 16.8711 6.9091 17.1226 7.1056 17.2537C7.3022 17.3848 7.5639 17.3592 7.7313 17.1924L9.7886 15.1351C10.4638 15.4299 11.1907 15.5882 11.9274 15.601H12.0762C14.1881 15.601 16.2887 14.1203 17.56 12.7229C17.9258 12.3128 17.9243 11.6932 17.5565 11.2849ZM14.139 12.6351C13.9202 13.3504 13.3636 13.9122 12.6503 14.1376C12.1437 14.299 11.5952 14.2653 11.1121 14.0434C11.0755 14.0256 11.0497 13.9912 11.043 13.9511C11.0363 13.911 11.0495 13.8701 11.0783 13.8414L13.8373 11.0829C13.8658 11.0538 13.9067 11.0404 13.9469 11.047C13.9871 11.0537 14.0216 11.0794 14.0393 11.1161C14.2581 11.593 14.2936 12.1337 14.139 12.6351Z'
        fill={color}
      />
      <Path
        d='M9.8531 12.5805C9.8654 12.6227 9.8538 12.6683 9.8229 12.6996L8.3119 14.2131C8.2684 14.2562 8.2004 14.2623 8.1501 14.2275C7.5288 13.7894 6.9568 13.2852 6.4441 12.7239C6.0729 12.3155 6.0744 11.6914 6.4475 11.2849C7.7313 9.8711 9.865 8.3785 11.9988 8.4073C12.5446 8.4042 13.0874 8.4876 13.6071 8.6544C13.6487 8.6681 13.6801 8.7024 13.6903 8.745C13.7004 8.7876 13.6877 8.8324 13.6567 8.8633L12.6935 9.827C12.6624 9.8581 12.6168 9.8699 12.5744 9.8577C12.3874 9.8028 12.1937 9.7739 11.9988 9.7719C10.7655 9.7719 9.7658 10.7716 9.7658 12.0049C9.7686 12.1998 9.7979 12.3935 9.8531 12.5805Z'
        fill={color}
      />
    </Svg>
  );
};

export const PasswordInput: React.FC<PasswordInputProps> = (props) =>
  useDsEnabled() ? <DsPasswordInput {...props} /> : <LegacyPasswordInput {...props} />;

const DsPasswordInput: React.FC<PasswordInputProps> = ({ testID, ...fieldProps }) => {
  const [isVisible, setIsVisible] = useState(false);
  const t = useT();

  return (
    <DsField
      autoCorrect={false}
      {...fieldProps}
      testID={testID}
      secureTextEntry={!isVisible}
      accessory={
        <BaseButton
          onPress={() => setIsVisible((v) => !v)}
          testID={testID ? testId(testID, 'toggle-visibility') : 'toggle-visibility-button'}
          accessibilityLabel={isVisible ? t('Hide password') : t('Show password')}
        >
          <ThemedText type='bodySmall'>{isVisible ? t('Hide') : t('Show')}</ThemedText>
        </BaseButton>
      }
    />
  );
};

const LegacyPasswordInput: React.FC<PasswordInputProps> = ({
  label,
  error,
  containerStyle,
  testID,
  labelAccessory: _labelAccessory,
  errorTestID: _errorTestID,
  ...inputProps
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const palette = useThemePalette();

  return (
    <View className='mb-6' style={containerStyle}>
      <ThemedText type='bodyBold' className='mb-2 text-login-purple-90'>
        {label}
      </ThemedText>
      <View className='relative w-full'>
        <TextInput
          className={cn(
            'w-full p-[14px] pr-[48px] border rounded-lg text-base text-login-purple-90 bg-login-surface-pure font-login-body',
            error ? 'border-login-red-50' : 'border-login-gray-10'
          )}
          secureTextEntry={!isVisible}
          placeholderTextColor={palette.gray[30]}
          autoCapitalize='none'
          autoCorrect={false}
          testID={testID}
          {...inputProps}
        />
        <BaseButton
          className='absolute right-4 top-0 bottom-0 justify-center p-1'
          onPress={() => setIsVisible((v) => !v)}
          testID={testID ? `${testID}-toggle-visibility` : 'toggle-visibility-button'}
          accessibilityLabel={isVisible ? 'Hide password' : 'Show password'}
        >
          <EyeIcon isVisible={isVisible} color={palette.purple[50]} />
        </BaseButton>
      </View>
      {error && (
        <ThemedText type='bodySmall' className='text-login-red-50 mt-2'>
          {error}
        </ThemedText>
      )}
    </View>
  );
};
